import {computed, defineComponent} from 'vue';

export default defineComponent({
    name: 'AegPagination',
    emits: ['page-change'],
    props: {
        pageSize: {
            type: Number,
            required: true,
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        total: {
            type: Number,
            required: true,
        },
        results: {
            type: Number,
            required: true,
        }
    },
    setup(props, {emit}) {
        
        const pagerFrom = computed<number>((): number => {
            return props.currentPage > 1
                ? (props.currentPage - 1) * (props.pageSize || props.results) + 1
                : 1;
        });

        const pagerTo = computed<number>((): number => {
            return Math.min(
                props.total ? props.total : props.results * props.currentPage,
                props.pageSize * props.currentPage
            );
        });

        const pagerTotal = computed<number>((): number => {
            return props.total || (props.results < props.pageSize ? props.results : 0);
        });

        const pagePosition = computed<string>((): string => {
            return `<span><strong>${pagerFrom.value}&nbsp;-&nbsp;${pagerTo.value ||
            "..."}</strong> van &nbsp;${pagerTotal.value ||
            "..."}</span>`;
        });

        const pageCount = computed<number>((): number => {
            return Math.ceil(props.total / (props.pageSize || 1));
        });

        const prevButtonActive = computed<boolean>((): boolean => {
            return props.currentPage !== 1;
        });

        const nextButtonActive = computed<boolean>((): boolean => {
            return props.currentPage !== pageCount.value;
        });

        const pagination = computed<string[]>((): string[] => {
            const VISIBLE_PAGES = 7;
            const EDGE_PAGES = 4;
            const ELLIPSIS = '...';

            if (pageCount.value <= 1) return [];

            const generateSequentialPages = (start: number, end: number): string[] =>
                Array.from({length: end - start + 1}, (_, i) => (start + i).toString());

            const generatePaginationForSmallRange = (): string[] =>
                generateSequentialPages(1, pageCount.value);

            const generatePaginationForLargeRange = (): string[] => {
                const isNearStart = props.currentPage <= EDGE_PAGES;
                const isNearEnd = pageCount.value - props.currentPage < EDGE_PAGES;
                const isMiddle = !isNearStart && !isNearEnd;

                const pages: string[] = ['1'];

                if (isNearStart) {
                    pages.push(...generateSequentialPages(2, 5));
                    pages.push(ELLIPSIS);
                } else if (isMiddle) {
                    pages.push(ELLIPSIS);
                    pages.push(...generateSequentialPages(props.currentPage - 1, props.currentPage + 1));
                    pages.push(ELLIPSIS);
                } else {
                    pages.push(ELLIPSIS);
                    pages.push(...generateSequentialPages(pageCount.value - 4, pageCount.value - 1));
                }

                pages.push(pageCount.value.toString());
                return pages;
            };

            return pageCount.value < VISIBLE_PAGES
                ? generatePaginationForSmallRange()
                : generatePaginationForLargeRange();
        });

        const isCurrentPage = (pageNr: string): boolean => {
            return props.currentPage + '' === pageNr;
        }

        const changePage = (pageNumber: number) => {
            if (!isNaN(pageNumber)) {
                emit('page-change', pageNumber);
            }
        }

        return {
            pagerFrom,
            pagerTo,
            pagerTotal,
            pagePosition,
            pageCount,
            prevButtonActive,
            nextButtonActive,
            pagination,
            changePage,
            isCurrentPage,
        }
    }
})
